<template>
  <b-sidebar
    id="site-form-sidebar"
    :visible="isSitesFormSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @shown="getData(sitesData)"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sites-form-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Site Form</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="close(hide)"
        />
      </div>
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <b-tabs content-class="mt-1" v-if="langs.length > 0">
            <b-tab v-for="(lang,key) in langs" :key="'tab-'+key"  :title="lang.name">
              <validation-provider
                #default="validationContext"
                :name="`site_name_${lang.code}`"
                rules="min:0"
              >
                <b-form-group 
                    label="Site Name" 
                    :label-for="`site_name_${lang.code}`"
                >
                    <quill-editor
                        :options="editorOptions"
                        :id="'site_name_' + lang.code"
                        v-model="Data.name[lang.code]"
                    />
                    <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider> 
              <validation-provider
                #default="validationContext"
                :name="`description_${lang.code}`"
                rules="min:0"
              >
                <b-form-group 
                    label="Description" 
                    :label-for="`description_${lang.code}`"
                >
                    <quill-editor
                        :options="editorOptions"
                        :id="'description_' + lang.code"
                        v-model="Data.description[lang.code]"
                    />
                    <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider> 
            </b-tab>
            <hr/>
            <validation-provider
              #default="validationContext"
              name="slug"
              rules="required"
            >
              <b-form-group label="Slug" label-for="site_name">
                <b-form-input
                  id="slug"
                  v-model="Data.slug"
                  autofocus
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="slug"
                  @input="changeSlug(Data.slug)"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <validation-provider
              #default="validationContext"
              :name="'hotel_code'"
            >
              <b-form-group
                label="Hotel Code"
                :label-for="'hotel_code'"
              >
                <b-form-input
                  :id="'hotel_code'"
                  v-model="Data.hotel_code"
                  autofocus
                  :state="getValidationState(validationContext)"
                  trim
                  :placeholder="'hotel code'"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <validation-provider
              #default="validationContext"
              name="domain"
              rules="required"
            >
              <b-form-group label="Domain" label-for="domain">
                <b-form-input
                  id="domain"
                  v-model="Data.domain"
                  autofocus
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="domain"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <validation-provider
              #default="validationContext"
              name="destination"
              rules="min:0"
            >
              <b-form-group label="Destination" label-for="destination">
                <v-select
                  id="destination"
                  v-model="Data.destination_id"
                  label="title"
                  :get-option-label="getOptionLabel"
                  placeholder="destination"
                  :reduce="(option) => option.id"
                  :options="destinationData.map(g => ({title: g.name[currentLocale], id: g.id}))"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <validation-provider
              #default="validationContext"
              name="Site Type"
              rules="required"
            >
              <b-form-group label="Site Type" label-for="sites_type">
                <v-select
                  id="site_type_id"
                  v-model="Data.site_types_id"
                  label="title"
                  placeholder="site type"
                  :reduce="(option) => option.id"
                  :options="typesData.map((type) => ({
                    title: type.name,
                    id: type.id
                  }))"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <div class="border rounded p-2">
              <h4 class="mb-1">Site Image</h4>
              <b-media
                no-body
                vertical-align="center"
                class="flex-column flex-md-row"
              >
                <b-media-aside>
                  <b-img
                    ref="refPreviewEl"
                    :src="Data.site_image"
                    height="75"
                    width="75"
                    class="rounded mr-2 mb-1 mb-md-0"
                  />
                </b-media-aside>
                <b-media-body>
                  <small class="text-muted">
                    Recommend image resolution 75x75.
                  </small>
                  <b-card-text class="my-50"> </b-card-text>
                  <div class="d-inline-block">
                    <b-form-file
                      ref="refInputEl"
                      v-model="Data.iconFile"
                      accept=".jpg, .png, .gif, .webp,"
                      placeholder="Choose file"
                      @input="inputImageRenderer"
                    />
                  </div>
                  <b-card-text class="my-50">
                    <b-button
                      v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                      variant="outline-primary"
                      v-if="Data.site_image"
                      @click="revomeImage"
                      >Remove Image</b-button
                    >
                  </b-card-text>
                </b-media-body>
              </b-media>
            </div>
            <br/>
            <validation-provider
              #default="validationContext"
              name="IsActive"
              rules="min:0"
            >
              <b-form-group label="Is Active" label-for="IsActive">
                <b-form-checkbox
                  id="isactive"
                  checked="true"
                  class="custom-control-success"
                  v-model="Data.is_active"
                  switch
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-tabs>
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="outline-success"
              class="mr-2"
              type="submit"
            >
              Save
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-danger"
              @click="close(hide)"
            >
              Cancel
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
  import { $themeConfig } from '@themeConfig'
  import {
    BSidebar, BForm, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BFormInvalidFeedback,
    BButton, BTabs, BTab, BMedia, BMediaAside, BImg, BMediaBody, BCardText, BFormFile,
  } from "bootstrap-vue";
  import { ValidationProvider, ValidationObserver } from "vee-validate";
  import { ref, onUnmounted } from "@vue/composition-api";
  import { required, alphaNum, email, password, confirmed } from "@validations";
  import formValidation from "@core/comp-functions/forms/form-validation";
  import Ripple from "vue-ripple-directive";
  import vSelect from "vue-select";
  import store from "@/store";
  import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
  import { useToast } from "vue-toastification/composition";
  import { useInputImageRenderer } from "@core/comp-functions/forms/form-utils";
  import { slugify } from '@/@fake-db/utils'
  import { quillEditor } from "vue-quill-editor";
  import Quill from 'quill'
  const sizeStyle = Quill.import('attributors/style/size')
  sizeStyle.whitelist = ['6px', '8px', '10px', '12px', '14px', '16px', '18px', '20px', '24px', '30px', '32px', '36px']
  Quill.register(sizeStyle, true)
  import "quill/dist/quill.snow.css";
  import "quill/dist/quill.core.css";
  import "quill/dist/quill.bubble.css";
  import languagesStoreModule from '@/views/languages/languagesStoreModule';

  export default {
      
    components: {
      BSidebar, BForm, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BFormInvalidFeedback,
      BButton, BTabs, BTab, ValidationObserver, ValidationProvider, vSelect, BMedia, BMediaAside,
      BImg, BMediaBody, BCardText, BFormFile, quillEditor,
    },

    directives: {
      Ripple,
    },

    model: {
      prop: "isSitesFormSidebarActive",
      event: "update:is-sites-form-sidebar-active",
    },

    props: {
      isSitesFormSidebarActive: {
        type: Boolean,
        required: true,
      },
      sitesData: {
        type: Object,
        required: false,
      },
        siteTypes: {
        type: Array,
        required: false,
      }
    },

    computed: {
      currentLocale(){
        return this.$i18n.locale;
      },
      editorOptions (){
        return $themeConfig.snowOption
      },
    },

    setup(props, {emit}){
      const {  appRef } = $themeConfig.app
      const LANGUAGES_STORE_MODULE_NAME = "app-languages";
      if (!store.hasModule(LANGUAGES_STORE_MODULE_NAME)) store.registerModule(LANGUAGES_STORE_MODULE_NAME, languagesStoreModule);
      onUnmounted(() => {
        if (store.hasModule(LANGUAGES_STORE_MODULE_NAME)) store.unregisterModule(LANGUAGES_STORE_MODULE_NAME);
      });
      const toast = useToast();
      const userStorage = JSON.parse(localStorage.getItem('userData'));
      const blankData = {
        id: null,
        name: {},
        description: {},
        slug: '',
        hotel_code: '',
        domain: '',
        site_image: '',
        site_types_id: null,
        destination_id: '',
        is_active: true,
        created_by: userStorage.id
      };

      const Data = ref(blankData);
      // const tabIndex = ref(0);
      const typesData = ref([])
      const refInputEl = ref(null);
      const refPreviewEl = ref(null);
      const langs = ref([]);
      const oldValue  =  ref(null)
      const destinationData = ref([])

      const { inputImageRenderer } = useInputImageRenderer(
        refInputEl,
        (base64) => {
          refPreviewEl.value.src = base64;
        }
      );

      const revomeImage = () => {
        refPreviewEl.value.src = require("@/assets/images/no-image.png");
      };

      const resetData = () => {
        Data.value = JSON.parse(JSON.stringify(blankData));
      };
      

      store.dispatch('app-sites/getSiteTypes')
          .then(response => {
            typesData.value = response.data.data
      })

      store.dispatch('app-sites/getDestinations', {
        keywords: ''
      })
        .then(response => {
          destinationData.value = response.data.data
      })

      const onSubmit = () => {
        Data.value.is_active = Data.value.is_active?1:0;
        Data.value.created_by = userStorage.id

        const formData = new FormData();
        formData.append('path', `/var/www/html/uploadfiles/uploads/${appRef}/amenities/`);
        formData.append('savepath', `uploads/${appRef}/amenities/`);
        formData.append('iconFile', refInputEl.value.files[0])
        formData.append('data', JSON.stringify(Data.value))

        if(Data.value.id) {
          store.dispatch("app-sites/updateSite", {
            id: Data.value.id,
            data: formData
          })
          .then((res) => {
            toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: `Success`,
                icon: "BellIcon",
                variant: "success",
                text: `${res.data.message}`,
              },
            });

            emit("refetch-data");
            emit("update:is-sites-form-sidebar-active", false);
          }).catch((error) => {
              toast({
                component: ToastificationContent,
                position: "top-right",
                props: {
                  title: `Error`,
                  icon: "BellIcon",
                  variant: "danger",
                  text: error.response.data.message,
                },
              });
            }
          );
        } else {

          store.dispatch("app-sites/addSite", {
            data: formData
          })
          .then((res) => {
            toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: `Success`,
                icon: "BellIcon",
                variant: "success",
                text: `${res.data.message}`,
              },
            });

            emit("refetch-data");
            emit("update:is-sites-form-sidebar-active", false);
          }).catch((error) => {
              toast({
                component: ToastificationContent,
                position: "top-right",
                props: {
                  title: `Error`,
                  icon: "BellIcon",
                  variant: "danger",
                  text: error.response.data.message,
                },
              });
            }
          )
        }
      }

      const getData = (data) => {
        Data.value.name = {}
        Data.value.description = {}
        Data.value.destination_id = ''
        Data.value.slug = ''
        Data.value.domain = ''
        Data.value.site_image = require(`@/assets/images/no-image.png`)
        Data.value.site_types_id = null
        Data.value.is_active = true

        if (data) {
          data.is_active = data.is_active?true:false;     
          data.site_image = data.site_image != ''? data.site_image: require(`@/assets/images/no-image.png`)

          Data.value  = data
          oldValue.value = JSON.parse(JSON.stringify({...data}))
        }
      };

      const refeshList = async () => {
        emit("refetch-data");
      };

      const getLangs = (filter)=>{
        store.dispatch('app-languages/fetchData', filter)
          .then((res) => {
            if(res.data.success){
                langs.value = res.data.data;
            }  
          }
        )
      }

      let filter = {
        is_active:1,
      }

      getLangs(filter);

      const changeSlug = (slug) => {
        Data.value.slug = slugify(slug)
      }

      const close = (hidden) => {
        for(const lang in oldValue.value?.name ?? {}) {
          Data.value.name[lang] = oldValue.value.name[lang]
        }

        hidden()
      }

      const {
        refFormObserver,
        getValidationState,
        resetForm
      } = formValidation(resetData);

      return {
        refFormObserver,
        getValidationState,
        resetForm,
        Data,
        // tabIndex,
        onSubmit,
        getData,
        typesData,
        refeshList,
        inputImageRenderer,
        refInputEl,
        refPreviewEl,
        revomeImage,
        getLangs,
        langs,
        changeSlug,
        close,
        destinationData,
      }
    },

    methods: {
      getOptionLabel(option) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = option.title;
        return tempDiv.textContent || tempDiv.innerText || "";
      },
    },
  };
</script>

<style lang="scss">
  @import "@core/scss/vue/libs/quill.scss";
</style>